import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  Button,
  HStack,
  Spinner,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Wrap,
} from '@chakra-ui/react';
import { Select } from '../Input';
import { fetchData } from '../../services/fetch';

/**
 * EG-42 / AD-3 — a seleção de templates que alimenta a estrutura derivada.
 *
 * Antes a escolha de template acontecia DENTRO de cada tema, depois de o gestor
 * já ter digitado quantos pilares e temas queria. Com a inversão a seleção vem
 * primeiro e vale para o questionário inteiro — é dela que a estrutura nasce.
 *
 * AD-3: os templates ACUMULAM. Selecionar um segundo template não substitui o
 * primeiro — 7 de 27 periódicos e 11 de 11 certificados com pilar usam 2+
 * templates, então substituir cobriria menos da metade dos casos reais.
 *
 * O componente não deduplica por `code`: dois templates podem trazer a mesma
 * pergunta com classificação independente (1.605 casos medidos). Quem deduplica,
 * e só por ID, é derivarEstruturaDeTemplates.
 */
export default function SelecaoDeTemplates({
  templates,
  templatesIsLoading,
  questoesSelecionadas = [],
  onChange,
  isDisabled = false,
}) {
  const [templateEscolhido, setTemplateEscolhido] = useState(null);

  const { data: questoesDoTemplate, isLoading: questoesCarregando } = useSWR(
    templateEscolhido ? `/question-template/template/${templateEscolhido}` : null,
    fetchData
  );

  const opcoesDeTemplate = useMemo(
    () =>
      (templates ?? []).map((template) => ({
        label: template?.name,
        value: template?.id_survey_template,
      })),
    [templates]
  );

  const jaSelecionadas = useMemo(
    () => new Set(questoesSelecionadas.map((q) => q.id_question_template)),
    [questoesSelecionadas]
  );

  const novasDoTemplate = useMemo(
    () =>
      (questoesDoTemplate ?? []).filter(
        (questao) => !jaSelecionadas.has(questao.id_question_template)
      ),
    [questoesDoTemplate, jaSelecionadas]
  );

  const nomeDoTemplate = (id) =>
    (templates ?? []).find((t) => String(t.id_survey_template) === String(id))
      ?.name ?? `Template ${id}`;

  const adicionarTemplate = () => {
    if (novasDoTemplate.length === 0) return;

    // Acrescenta ao fim: a ordem de seleção é o que vira o index global no
    // backend (AD-6), então reordenar aqui faria a tela discordar do que é gravado.
    onChange?.([
      ...questoesSelecionadas,
      ...novasDoTemplate.map((questao) => ({
        ...questao,
        id_survey_template: templateEscolhido,
      })),
    ]);
  };

  const removerTemplate = (idTemplate) => {
    onChange?.(
      questoesSelecionadas.filter(
        (questao) => String(questao.id_survey_template) !== String(idTemplate)
      )
    );
  };

  const templatesUsados = useMemo(() => {
    const contagem = new Map();
    for (const questao of questoesSelecionadas) {
      const id = questao.id_survey_template;
      contagem.set(id, (contagem.get(id) ?? 0) + 1);
    }
    return [...contagem.entries()];
  }, [questoesSelecionadas]);

  if (templatesIsLoading) return <Spinner />;

  return (
    <Stack spacing='4'>
      <HStack spacing='6' align='end'>
        <Select
          label='Template'
          placeholder='Selecione um template'
          value={templateEscolhido ?? ''}
          options={opcoesDeTemplate}
          isDisabled={isDisabled}
          onChange={(evento) => setTemplateEscolhido(evento.target.value)}
        />
        <Button
          variant='primary'
          size='medium'
          minW='137px'
          isDisabled={
            isDisabled ||
            !templateEscolhido ||
            questoesCarregando ||
            novasDoTemplate.length === 0
          }
          onClick={adicionarTemplate}
        >
          Adicionar
        </Button>
      </HStack>

      {templateEscolhido && !questoesCarregando && novasDoTemplate.length === 0 && (
        <Text fontSize='sm' color='#5C6066'>
          Todas as perguntas deste template já estão no questionário.
        </Text>
      )}

      {templatesUsados.length > 0 && (
        <Wrap>
          {templatesUsados.map(([idTemplate, total]) => (
            // CORES EXPLÍCITAS, SEM colorScheme — e aqui o motivo é medido.
            //
            // colorScheme='blue' com variant subtle usa blue.100 como FUNDO, e
            // neste tema blue.100 é #0062FF: cor de MARCA saturada num slot que
            // o Chakra trata como tint. Daí o azul forte que o dev apontou.
            //
            // É a quinta ocorrência deste contorno no ecossistema (EG-40 red.100
            // pintando alerta de erro de verde, EG-41 Tag, esg-starter, EG-42
            // AvisoInline, agora esta). A causa raiz é a paleta e merece ticket
            // próprio; enquanto ela existir, componente novo não usa colorScheme.
            //
            // #0073BB é a MESMA cor da linha da aba selecionada
            // (styles.scss, .tab-rounded-border[aria-selected='true']). O badge
            // marca o que alimenta as abas, então falar a mesma língua visual das
            // abas não é escolha estética solta.
            <Tag
              key={idTemplate}
              // backgroundColor e não bg: `bg` virou a shorthand `background`,
              // que o jsdom não expande, e o teste que guarda esta decisão não
              // conseguia enxergar a cor. Mesmo resultado no navegador.
              backgroundColor='white'
              color='#292B30'
              border='1px solid #0073BB'
              borderRadius='full'
            >
              <TagLabel>
                {nomeDoTemplate(idTemplate)} · {total}
              </TagLabel>
              {!isDisabled && (
                <TagCloseButton
                  // Sem cor explícita o botão herda o texto com opacidade e,
                  // sobre fundo branco, fica quase invisível.
                  color='#5C6066'
                  opacity={1}
                  _hover={{ bg: '#E6F1F8', color: '#0073BB' }}
                  onClick={() => removerTemplate(idTemplate)}
                />
              )}
            </Tag>
          ))}
        </Wrap>
      )}
    </Stack>
  );
}
